import React, { useMemo } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet'
import { MarketViewSearchResult } from '../../services/api'
import { LatLngExpression } from 'leaflet'
import VendorMapPopup from './VendorMapPopup'
import { describeOverall, type ScoreDisplay } from '../../lib/scoreDisplay'
import 'leaflet/dist/leaflet.css'

interface MapViewProps {
  results: MarketViewSearchResult[]
}

const MapView: React.FC<MapViewProps> = ({ results }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Group products by vendor, keep only best product per vendor
  const vendorLocations = useMemo(() => {
    const vendorMap = new Map<number, any>()

    results.forEach(result => {
      const { vendor, product, scores } = result
      
      // Skip vendors without coordinates
      if (!vendor.latitude || !vendor.longitude) {
        return
      }

      const existing = vendorMap.get(vendor.id)
      const overall = describeOverall(scores)

      // Keep product with highest overall score. An unmeasured rollup ranks
      // below every real one rather than competing with its placeholder value
      // (DEV-584) - but it still wins an empty slot, so the vendor stays on the
      // map with an honest marker.
      const rank = overall.isScored ? overall.value : -1

      if (!existing || rank > existing.rank) {
        vendorMap.set(vendor.id, {
          vendor,
          bestProduct: product,
          scores,
          overall,
          rank,
          result,
          position: [vendor.latitude, vendor.longitude] as LatLngExpression
        })
      }
    })

    return Array.from(vendorMap.values())
  }, [results])

  // Center map on US
  const defaultCenter: LatLngExpression = [39.8283, -98.5795]
  const defaultZoom = 4

  // Get color based on overall score. Unmeasured is grey: the marker has to be
  // drawn somewhere, but it must not read as a red 'bad vendor' (DEV-584).
  const getMarkerColor = (score: ScoreDisplay) => {
    if (!score.isScored) return '#9ca3af' // neutral grey
    if (score.value >= 80) return '#4ade80' // green
    if (score.value >= 60) return '#60a5fa' // blue
    if (score.value >= 40) return '#fbbf24' // yellow
    return '#f87171' // red
  }

  return (
    <div ref={containerRef} className="absolute inset-0">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        className="absolute inset-0"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {vendorLocations.map(({ vendor, bestProduct, scores, overall, position }) => (
          <CircleMarker
            key={vendor.id}
            center={position}
            radius={12}
            fillColor={getMarkerColor(overall)}
            fillOpacity={0.7}
            stroke={true}
            color="#fff"
            weight={2}
          >
            <Popup>
              <VendorMapPopup
                vendor={vendor}
                product={bestProduct}
                scores={scores}
              />
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 sm:bottom-6 sm:left-6 bg-card border border-border rounded-lg shadow-lg p-2 sm:p-3 z-[1000] max-w-[45%] sm:max-w-none">
        <h4 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2">Score</h4>
        <div className="space-y-0.5 sm:space-y-1">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#4ade80] flex-shrink-0"></div>
            <span>80-100</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#60a5fa] flex-shrink-0"></div>
            <span>60-79</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#fbbf24] flex-shrink-0"></div>
            <span>40-59</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#f87171] flex-shrink-0"></div>
            <span>0-39</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MapView
